import { getThemeForCharacter } from "./content";
import { SAVED_MATH_SKILL_IDS } from "./types";
import type {
  ArithmeticQuestion,
  CharacterId,
  Checkpoint,
  CollectibleInventory,
  GameSession,
  GameSettings,
  LevelSummary,
  MathAnswer,
  MathDifficulty,
  MathProgress,
  Position,
  SavedMathSkillId,
  SavedMathSkillMastery,
  WorldId,
} from "./types";

export const DEFAULT_SETTINGS: GameSettings = {
  locale: "he-IL",
  musicVolume: 0.7,
  effectsVolume: 0.8,
  muted: false,
  reducedMotion: false,
  highContrast: false,
  touchControls: true,
};

export const EMPTY_COLLECTIBLES: CollectibleInventory = {
  coins: 0,
  gems: 0,
  wisdomStars: 0,
  hearts: 0,
};

export function createDefaultMathProgress(difficulty: MathDifficulty = 1): MathProgress {
  const masteryBySkill = Object.fromEntries(
    SAVED_MATH_SKILL_IDS.map((skill) => [
      skill,
      {
        currentTier: difficulty,
        recentResults: [],
        consecutiveMisses: 0,
        supportActive: false,
        totalAttempts: 0,
        totalCorrect: 0,
      } satisfies SavedMathSkillMastery,
    ]),
  ) as unknown as Record<SavedMathSkillId, SavedMathSkillMastery>;

  return {
    difficulty,
    correctAnswers: 0,
    incorrectAnswers: 0,
    recentResults: [],
    masteryBySkill,
  };
}

export function copyMathProgress(progress: MathProgress): MathProgress {
  return {
    ...progress,
    recentResults: [...progress.recentResults],
    masteryBySkill: Object.fromEntries(
      SAVED_MATH_SKILL_IDS.map((skill) => [
        skill,
        {
          ...progress.masteryBySkill[skill],
          recentResults: [...progress.masteryBySkill[skill].recentResults],
        },
      ]),
    ) as unknown as Record<SavedMathSkillId, SavedMathSkillMastery>,
  };
}

export const DEFAULT_MATH_PROGRESS: MathProgress = createDefaultMathProgress();

export const START_CHECKPOINT: Checkpoint = {
  id: "start",
  index: 0,
  position: { x: 96, y: 360 },
};

export interface GameplayRules {
  readonly damageInvulnerabilityMs: number;
  readonly respawnInvulnerabilityMs: number;
  readonly questionResumeInvulnerabilityMs: number;
}

export const DEFAULT_GAMEPLAY_RULES: GameplayRules = {
  damageInvulnerabilityMs: 2_000,
  respawnInvulnerabilityMs: 2_500,
  questionResumeInvulnerabilityMs: 1_000,
};

interface StateBase {
  readonly settings: GameSettings;
}

export interface MenuState extends StateBase {
  readonly phase: "menu";
}

export interface CharacterSelectState extends StateBase {
  readonly phase: "character-select";
}

export interface LoadingState extends StateBase {
  readonly phase: "loading";
  readonly characterId: CharacterId;
  readonly worldId: WorldId;
  readonly levelNumber: number;
  readonly mathProgress: MathProgress;
}

export interface PlayingState extends StateBase {
  readonly phase: "playing";
  readonly session: GameSession;
}

export interface PausedState extends StateBase {
  readonly phase: "paused";
  readonly session: GameSession;
}

export interface QuestionContext {
  readonly boxId: string;
  readonly question: ArithmeticQuestion;
  readonly attempts: number;
}

export interface QuestionOpeningState extends StateBase {
  readonly phase: "question-opening";
  readonly session: GameSession;
  readonly question: QuestionContext;
}

export interface QuestionActiveState extends StateBase {
  readonly phase: "question-active";
  readonly session: GameSession;
  readonly question: QuestionContext;
}

export interface QuestionFeedbackState extends StateBase {
  readonly phase: "question-feedback";
  readonly session: GameSession;
  readonly question: QuestionContext;
  readonly outcome: "correct" | "wrong";
  readonly selectedAnswer: MathAnswer;
}

export interface LevelCompleteState extends StateBase {
  readonly phase: "level-complete";
  readonly session: GameSession;
  readonly summary: LevelSummary;
}

export type GameState =
  | MenuState
  | CharacterSelectState
  | LoadingState
  | PlayingState
  | PausedState
  | QuestionOpeningState
  | QuestionActiveState
  | QuestionFeedbackState
  | LevelCompleteState;

function clampVolume(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

export function normalizeSettings(
  settings: Partial<GameSettings> = {},
): GameSettings {
  return {
    locale: "he-IL",
    musicVolume: clampVolume(
      settings.musicVolume ?? DEFAULT_SETTINGS.musicVolume,
    ),
    effectsVolume: clampVolume(
      settings.effectsVolume ?? DEFAULT_SETTINGS.effectsVolume,
    ),
    muted: settings.muted ?? DEFAULT_SETTINGS.muted,
    reducedMotion: settings.reducedMotion ?? DEFAULT_SETTINGS.reducedMotion,
    highContrast: settings.highContrast ?? DEFAULT_SETTINGS.highContrast,
    touchControls: settings.touchControls ?? DEFAULT_SETTINGS.touchControls,
  };
}

export function createInitialGameState(
  settings: Partial<GameSettings> = {},
): MenuState {
  return { phase: "menu", settings: normalizeSettings(settings) };
}

export interface CreateSessionOptions {
  readonly characterId: CharacterId;
  readonly levelNumber?: number;
  readonly levelId?: string;
  readonly checkpoint?: Checkpoint;
  readonly mathProgress?: MathProgress;
}

export function createGameSession({
  characterId,
  levelNumber = 1,
  levelId = `${characterId}-${levelNumber}`,
  checkpoint = START_CHECKPOINT,
  mathProgress = DEFAULT_MATH_PROGRESS,
}: CreateSessionOptions): GameSession {
  const theme = getThemeForCharacter(characterId);
  return {
    characterId,
    worldId: theme.id,
    levelId,
    levelNumber,
    health: 3,
    maxHealth: 3,
    invulnerableUntilMs: 0,
    checkpoint: copyCheckpoint(checkpoint),
    respawn: null,
    collectibles: { ...EMPTY_COLLECTIBLES },
    collectedItemIds: [],
    openedQuestionBoxIds: [],
    solvedQuestionBoxIds: [],
    mathProgress: copyMathProgress(mathProgress),
  };
}

export function copyPosition(position: Position): Position {
  return { x: position.x, y: position.y };
}

export function copyCheckpoint(checkpoint: Checkpoint): Checkpoint {
  return {
    id: checkpoint.id,
    index: checkpoint.index,
    position: copyPosition(checkpoint.position),
  };
}

export function calculateLevelScore(session: GameSession): number {
  return (
    session.collectibles.coins * 10 +
    session.collectibles.gems * 100 +
    session.collectibles.wisdomStars * 200
  );
}

export function isWorldFrozen(state: GameState): boolean {
  return state.phase !== "playing";
}

export function hasActiveSession(
  state: GameState,
): state is Exclude<GameState, MenuState | CharacterSelectState | LoadingState> {
  return "session" in state;
}
